'use client';

import { useCallback, useEffect, useMemo, useRef } from 'react';
import {
  computeGroupCheckboxState,
  GROUP_CHECKBOX_STATE,
  isArchivedLifecycle,
  toggleIdsInSet,
  type GroupCheckboxState,
  type InternalEnrichedFinding,
} from '@terra/ui-design-system';
import type { TargetTypeType } from '@terra/shared-schemas-orchestrator';
import type {
  FindingsBulkFilter,
  FindingsBulkFilterPayload,
} from '../../../clients/testerClient';
import type { FindingsSelectionState } from '../../../store/tab/findingsTabScopedFilterStore';
import { useFindingsSelectionCount } from '../../../hooks/api/useFindings';

export type FindingsSelection =
  | { mode: 'ids'; ids: Set<string> }
  | {
      mode: 'filter';
      filter: FindingsBulkFilter;
      excludedIds: Set<string>;
      totalMatching: number;
    };

const EMPTY_SELECTION: FindingsSelection = { mode: 'ids', ids: new Set() };

const toPersistedSelection = (
  selection: FindingsSelection,
): FindingsSelectionState =>
  selection.mode === 'ids'
    ? { mode: 'ids', ids: Array.from(selection.ids) }
    : {
        mode: 'filter',
        filter: selection.filter,
        excludedIds: Array.from(selection.excludedIds),
        totalMatching: selection.totalMatching,
      };

const fromPersistedSelection = (
  selection: FindingsSelectionState,
): FindingsSelection =>
  selection.mode === 'ids'
    ? { mode: 'ids', ids: new Set(selection.ids) }
    : {
        mode: 'filter',
        filter: selection.filter,
        excludedIds: new Set(selection.excludedIds),
        totalMatching: selection.totalMatching,
      };

const resolveHeaderCheckboxState = ({
  selection,
  loadedActionableIds,
}: {
  selection: FindingsSelection;
  loadedActionableIds: readonly string[];
}): GroupCheckboxState => {
  if (selection.mode === 'filter') {
    const effectiveSelected = Math.max(
      0,
      selection.totalMatching - selection.excludedIds.size,
    );

    if (effectiveSelected === 0) return GROUP_CHECKBOX_STATE.UNCHECKED;

    return effectiveSelected === selection.totalMatching
      ? GROUP_CHECKBOX_STATE.CHECKED
      : GROUP_CHECKBOX_STATE.INDETERMINATE;
  }

  if (selection.ids.size === 0) return GROUP_CHECKBOX_STATE.UNCHECKED;

  return computeGroupCheckboxState({
    itemIds: loadedActionableIds,
    isItemSelected: (id) => selection.ids.has(id),
  }) === GROUP_CHECKBOX_STATE.CHECKED
    ? GROUP_CHECKBOX_STATE.CHECKED
    : GROUP_CHECKBOX_STATE.INDETERMINATE;
};

export function useFindingsSelection({
  targetId,
  targetType,
  filter,
  findings,
  persistedSelection,
  setPersistedSelection,
  resetKey,
}: {
  targetId: string | null | undefined;
  targetType: TargetTypeType;
  filter: FindingsBulkFilter;
  findings: readonly InternalEnrichedFinding[];
  persistedSelection: FindingsSelectionState;
  setPersistedSelection: (selection: FindingsSelectionState) => void;
  resetKey: string;
}) {
  const selection = useMemo(
    () => fromPersistedSelection(persistedSelection),
    [persistedSelection],
  );
  const selectionRef = useRef(selection);

  selectionRef.current = selection;

  const setSelection = useCallback(
    (
      next:
        | FindingsSelection
        | ((previous: FindingsSelection) => FindingsSelection),
    ) => {
      const resolved =
        typeof next === 'function' ? next(selectionRef.current) : next;

      selectionRef.current = resolved;
      setPersistedSelection(toPersistedSelection(resolved));
    },
    [setPersistedSelection],
  );
  const clearSelection = useCallback(
    () => setSelection(EMPTY_SELECTION),
    [setSelection],
  );
  const previousResetKeyRef = useRef(resetKey);

  useEffect(() => {
    if (previousResetKeyRef.current === resetKey) {
      return;
    }

    previousResetKeyRef.current = resetKey;
    clearSelection();
  }, [clearSelection, resetKey]);

  const loadedActionableIds = useMemo(
    () =>
      findings
        .filter((finding) => !isArchivedLifecycle(finding.lifecycle))
        .map((finding) => finding.id),
    [findings],
  );
  const { data: selectedCountByFilter = null } = useFindingsSelectionCount({
    targetId,
    targetType,
    filter: selection.mode === 'filter' ? selection.filter : filter,
    excludedIds:
      selection.mode === 'filter' ? Array.from(selection.excludedIds) : [],
    enabled: selection.mode === 'filter',
  });
  const selectedCount =
    selection.mode === 'ids'
      ? selection.ids.size
      : (selectedCountByFilter ??
        Math.max(0, selection.totalMatching - selection.excludedIds.size));
  const isRowSelected = useCallback(
    (id: string) =>
      selection.mode === 'ids'
        ? selection.ids.has(id)
        : !selection.excludedIds.has(id),
    [selection],
  );
  const setIdsSelected = useCallback(
    ({ ids, selected }: { ids: readonly string[]; selected: boolean }) =>
      setSelection((previous) =>
        previous.mode === 'ids'
          ? {
              mode: 'ids',
              ids: toggleIdsInSet({
                ids: previous.ids,
                itemIds: ids,
                shouldInclude: selected,
              }),
            }
          : {
              ...previous,
              excludedIds: toggleIdsInSet({
                ids: previous.excludedIds,
                itemIds: ids,
                shouldInclude: !selected,
              }),
            },
      ),
    [setSelection],
  );
  const selectAllLoaded = useCallback(
    (selected: boolean) =>
      setSelection(
        selected
          ? { mode: 'ids', ids: new Set(loadedActionableIds) }
          : EMPTY_SELECTION,
      ),
    [loadedActionableIds, setSelection],
  );
  const buildBulkFilterPayload = useCallback((): FindingsBulkFilterPayload => {
    if (!targetId) {
      throw new Error('Missing targetId');
    }

    if (selection.mode !== 'filter') {
      throw new Error('Selection is not in filter mode');
    }

    return {
      applicationId: targetId,
      filter: {
        ...selection.filter,
        lifecycleState:
          selection.filter.lifecycleState === 'all'
            ? 'active'
            : selection.filter.lifecycleState,
      },
      excludedIds: Array.from(selection.excludedIds),
    };
  }, [selection, targetId]);

  return {
    selection,
    selectedCount,
    hasBulkSelection: selectedCount > 1,
    headerCheckboxState: resolveHeaderCheckboxState({
      selection,
      loadedActionableIds,
    }),
    hasLoadedActionableFindings: loadedActionableIds.length > 0,
    isRowSelected,
    setIdsSelected,
    selectAllLoaded,
    clearSelection,
    buildBulkFilterPayload,
  };
}

export type FindingsSelectionModel = ReturnType<typeof useFindingsSelection>;
